import { AHSeparator, useBoxStyle, useTheme, useUploadImage } from "arkh";
import React, { memo, useCallback, useEffect, useMemo, useRef } from "react";
import { graphql, useMutation } from "react-relay";

import { RoutineEditChecklistTaskItemGenerateUploadLinkMutation } from "./__generated__/RoutineEditChecklistTaskItemGenerateUploadLinkMutation.graphql";
import {
  Platform,
  Pressable,
  PressableStateCallbackType,
  StyleProp,
  TextInput,
  ViewStyle,
} from "react-native";
import Color from "color";
import * as Haptics from "expo-haptics";
import { Images } from "./Images";
import { InstructionsInput } from "./InstructionsInput";
import { Toolbar } from "./Toolbar";
import { Body } from "./Body";
import { useImageActionSheet } from "../../../../components/mobile/useImageActionSheet";

type Props = {
  index: number;
  taskId: string;
  sectionId: string;
  title: string;
  instructions: string;
  autoFocus?: boolean;
  images: { id: string; uri: string; width: number; height: number }[];
  selectedTask: boolean;
  itemType: "TASK";
  taskType: "CHECK" | "NUMBER" | "OPTIONS" | "TEXT";
  isOnlyTaskInstance: boolean;
  isLast: boolean;
  isFirst: boolean;
  orgTopic: {
    id: string;
    name: string;
    isPrivate: boolean;
  } | null;
  canBeDisabled: boolean;
  onSetTitle: (id: string, title: string) => void;
  onSetInstructions: (id: string, instructons: string) => void;
  onAddImage: (
    taskId: string,
    image: {
      id: string;
      uri: string;
      width: number;
      height: number;
    }
  ) => void;
  onRemoveItem(_: {
    taskId: string;
    sectionId: string;
    isOnlyTaskInstance: boolean;
  }): void;
  onPressTaskMore(taskId: string): void;
  onRemoveImage: (taskId: string, imageId: string) => void;

  onSetSelected(
    item: { taskId: string; sectionId: string },
    index: number
  ): void;
};

export const RoutineEditChecklistTaskItem = ({
  onSetSelected: _onSetSelected,
  ...props
}: Props) => {
  const onSetSelected = useCallback(
    (item: { taskId: string; sectionId: string }, index: number) => {
      _onSetSelected(item, index);
    },
    [_onSetSelected]
  );

  return (
    <MemoizedRoutineEditChecklistTaskItem
      {...props}
      onSetSelected={onSetSelected}
    />
  );
};

const InnerComponent = ({
  index,
  taskId,
  sectionId,
  title,
  instructions,
  taskType,
  selectedTask,
  itemType,
  orgTopic,
  canBeDisabled,
  images,
  isOnlyTaskInstance,
  isFirst,
  isLast,
  onSetTitle,
  onSetInstructions,
  onAddImage,
  onRemoveItem,
  onPressTaskMore,
  onRemoveImage,
  onSetSelected,
  autoFocus,
}: Props) => {
  const theme = useTheme();
  const selectedItem = useMemo(() => selectedTask, [selectedTask]);

  const boxStyle = useBoxStyle({
    cornerRadiusBottomLeft: isLast ? "large" : "none",
    cornerRadiusBottomRight: isLast ? "large" : "none",
    cornerRadiusTopLeft: isFirst ? "large" : "none",
    cornerRadiusTopRight: isFirst ? "large" : "none",
    spaceVertical: "medium",
  });
  const backgroundColor = useCallback(
    (pressed: boolean) => {
      return pressed
        ? Color(theme.primary).lighten(0.95).toString()
        : selectedTask
          ? Color(theme.primary).lighten(0.9).toString()
          : theme.surface;
    },
    [selectedTask, theme.primary, theme.surface]
  );
  const collapsedInstructions = useMemo(() => {
    const inst = instructions.trim().split("\n");
    if (inst.length > 1) {
      return inst[0] + "…";
    }
    return instructions;
  }, [instructions]);
  const style = useCallback(
    ({ pressed }: PressableStateCallbackType) => {
      const bgColor = backgroundColor(pressed);
      return [
        boxStyle,
        {
          overflow: "hidden",
          borderTopWidth: isFirst ? 1 : 0,
          borderLeftWidth: 1,
          borderRightWidth: 1,
          borderBottomWidth: isLast ? 1 : 0,
          borderColor:
            pressed || selectedItem ? bgColor : theme["border-primary"],
          backgroundColor: bgColor,
        },
      ] satisfies StyleProp<ViewStyle>;
    },
    [boxStyle, isFirst, isLast, selectedItem, backgroundColor, theme]
  );
  const [generateUploadLink] =
    useMutation<RoutineEditChecklistTaskItemGenerateUploadLinkMutation>(graphql`
      mutation RoutineEditChecklistTaskItemGenerateUploadLinkMutation {
        uploadImageLink {
          image {
            id
          }
          url
        }
      }
    `);

  const { uploadImage, prepareForImageUpload } = useUploadImage();

  const addImage = useCallback(
    async (
      uri: string,
      { width, height }: { width: number; height: number }
    ) => {
      prepareForImageUpload({
        width,
        height,
        localImageURI: uri,
      }).then((uploadId) => {
        generateUploadLink({
          variables: {},
          onCompleted: (data) => {
            if (data.uploadImageLink) {
              const id = data.uploadImageLink.image.id;
              uploadImage({
                imageId: id,
                uploadURL: data.uploadImageLink.url,
                uploadId: uploadId,
              });
              onAddImage(taskId, { id, uri, width, height });
            }
          },
        });
      });
    },
    [generateUploadLink, onAddImage, prepareForImageUpload, taskId, uploadImage]
  );
  const [openImageActionSheet, anchor] = useImageActionSheet({
    onAddImage: addImage,
  });
  const setTitle = useCallback(
    (text: string) => onSetTitle(taskId, text),
    [onSetTitle, taskId]
  );
  const setInstructions = useCallback(
    (instructions: string) => onSetInstructions(taskId, instructions),
    [onSetInstructions, taskId]
  );
  const ref = useRef<TextInput>(null);
  useEffect(() => {
    if (autoFocus) {
      ref.current?.focus();
    }
  }, [autoFocus]);

  const onPress = useCallback(() => {
    if (Platform.OS === "ios") {
      Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
    }

    ref.current?.focus();
  }, []);

  return (
    <>
      {!isFirst ? <AHSeparator /> : null}

      <Pressable
        disabled={selectedItem}
        pointerEvents={"auto"}
        onPress={onPress}
        style={style}
        {...{ tabIndex: -1 }}
      >
        <Body
          canBeDisabled={canBeDisabled}
          ref={ref}
          orgTopic={orgTopic}
          collapsedInstruction={collapsedInstructions}
          hasImages={images.length > 0}
          selectedSectionTask={selectedItem}
          taskType={taskType}
          title={title}
          taskId={taskId}
          onPressTaskType={onPressTaskMore}
          onChangeTitle={setTitle}
          onFocus={() => {
            onSetSelected(
              {
                taskId,
                sectionId,
              },
              index
            );
          }}
        />

        {selectedItem ? (
          <>
            <InstructionsInput
              instructions={instructions}
              setInstructions={setInstructions}
            />
            {images.length > 0 ? (
              <Images
                images={images.map((image) => ({
                  ...image,
                }))}
                onRemoveImage={onRemoveImage}
                taskId={taskId}
                ref={anchor as any}
                openImageActionSheet={openImageActionSheet}
              />
            ) : null}
            <Toolbar
              taskId={taskId}
              anchor={anchor}
              isOnlyTaskInstance={isOnlyTaskInstance}
              numberOfImages={images.length}
              onRemoveItem={onRemoveItem}
              onSettingsPress={onPressTaskMore}
              openImageActionSheet={openImageActionSheet}
              sectionId={sectionId}
            />
          </>
        ) : null}
      </Pressable>
    </>
  );
};

const MemoizedRoutineEditChecklistTaskItem = memo(InnerComponent);
